import { ZoneNote } from "@/api/v1/flight-restriction-zone-types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Globe, FileText } from "lucide-react";

interface ZoneNotesProps {
  notes: ZoneNote[];
}

export const ZoneNotes: React.FC<ZoneNotesProps> = ({ notes }) => {
  if (!notes || notes.length === 0) {
    return null;
  }

  // Helper to safely get string value from note object
  const getNoteValue = (note: ZoneNote, key: string): string | undefined => {
    const value = note[key];
    if (typeof value === 'string') return value;
    if (value != null) return String(value);
    return undefined;
  };

  // Helper to get the main text content - try common field names
  const getNoteText = (note: ZoneNote): string => {
    return getNoteValue(note, 'Text') || 
           getNoteValue(note, 'text') || 
           getNoteValue(note, 'content') || 
           getNoteValue(note, 'Content') ||
           getNoteValue(note, 'description') ||
           getNoteValue(note, 'Description') ||
           '';
  };

  // Helper to get the purpose/title - try common field names
  const getNotePurpose = (note: ZoneNote): string => {
    return getNoteValue(note, 'Purpose') || 
           getNoteValue(note, 'purpose') || 
           getNoteValue(note, 'title') || 
           getNoteValue(note, 'Title') ||
           getNoteValue(note, 'type') ||
           getNoteValue(note, 'Type') ||
           'Note';
  };

  // Helper to get the language
  const getNoteLang = (note: ZoneNote): string | undefined => {
    return getNoteValue(note, 'Lang') || 
           getNoteValue(note, 'lang') || 
           getNoteValue(note, 'language') ||
           getNoteValue(note, 'Language');
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-muted-foreground mb-2 block">
          Notes
        </label>
        <div className="space-y-3">
          {notes.map((note, index) => {
            const text = getNoteText(note);
            const purpose = getNotePurpose(note);
            const lang = getNoteLang(note);

            // If no text content, show all fields as key-value pairs
            if (!text) {
              return (
                <Card key={index} className="border-l-4 border-l-primary">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <CardTitle className="text-sm font-medium">
                          Note {index + 1}
                        </CardTitle>
                      </div>
                      {lang && (
                        <Badge variant="outline" className="text-xs">
                          <Globe className="h-3 w-3 mr-1" />
                          {lang.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-2 text-sm">
                      {Object.entries(note).map(([key, value]) => (
                        <div key={key} className="flex flex-col gap-1">
                          <span className="font-medium text-muted-foreground capitalize">
                            {key.replace(/_/g, ' ')}:
                          </span>
                          <span className="whitespace-pre-line">
                            {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            }

            return (
              <Card key={index} className="border-l-4 border-l-primary">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <CardTitle className="text-sm font-medium">
                        {purpose}
                      </CardTitle>
                    </div>
                    {lang && (
                      <Badge variant="outline" className="text-xs">
                        <Globe className="h-3 w-3 mr-1" />
                        {lang.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-sm whitespace-pre-line">
                    {text}
                  </CardDescription>
                  {/* Show other fields if they exist */}
                  {Object.keys(note).filter(key => 
                    !['Text', 'text', 'content', 'Content', 'Purpose', 'purpose', 'Lang', 'lang', 'language', 'Language'].includes(key)
                  ).length > 0 && (
                    <div className="mt-3 pt-3 border-t space-y-1 text-xs text-muted-foreground">
                      {Object.entries(note)
                        .filter(([key]) => 
                          !['Text', 'text', 'content', 'Content', 'Purpose', 'purpose', 'Lang', 'lang', 'language', 'Language'].includes(key)
                        )
                        .map(([key, value]) => (
                          <div key={key} className="flex gap-2">
                            <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>
                            <span>{typeof value === 'string' ? value : JSON.stringify(value)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

